// features/comments/CommentList.js

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from '../../services/axios';
import CommentForm from './CommentForm';
import { useThemeContext } from '../../context/ThemeContext';

const Section = styled.div`
  font-family: inherit;
  margin-top: 10px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary || '#007bff'};
  cursor: pointer;
  font-weight: bold;
  font-size: 0.95rem;
  margin-bottom: 10px;
`;

const CommentItem = styled.div`
  margin-bottom: 1.2rem;
  padding: 8px;
  background-color: ${({ theme }) => theme.cardInner || '#f9f9f9'};
  border-left: 3px solid ${({ theme }) => theme.primary || '#007bff'};
  border-radius: 4px;
`;

const Author = styled.div`
  font-weight: bold;
  color: ${({ theme }) => theme.text || '#000'};
`;

const Timestamp = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.subtleText || '#666'};
  margin-top: 4px;
`;

const Content = styled.p`
  margin-top: 10px; /* increased space between date and text */
  white-space: pre-line;
`;

const CommentsWrapper = styled.div`
  margin-top: 10px;
`;

const formatDate = (timestamp) => {
  const options = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  return new Date(timestamp).toLocaleString(undefined, options);
};

const CommentList = ({ postId, user, allowComments }) => {
  const [comments, setComments] = useState([]);
  const [expanded, setExpanded] = useState(true); // 👈 Default to expanded
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { theme } = useThemeContext();

  useEffect(() => {
    if (!postId) return;
    axios.get(`/api/comments/?post=${postId}`)
      .then(res => setComments(res.data))
      .catch(err => console.error('Failed to fetch comments:', err));
  }, [postId, refreshTrigger]);

  const handleCommentPosted = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (!allowComments) return null;

  return (
    <Section theme={theme}>
      <ToggleButton onClick={() => setExpanded(prev => !prev)} theme={theme}>
        {expanded ? 'Hide Comments' : `Show Comments (${comments.length})`}
      </ToggleButton>

      {expanded && (
        <CommentsWrapper>
          {comments.length === 0 && (
            <p style={{ fontStyle: 'italic', color: theme.subtleText || '#999' }}>No comments yet.</p>
          )}
          {comments.map(comment => (
            <CommentItem key={comment.id} theme={theme}>
            <Author theme={theme}>
              {(comment.author_display || comment.author) || 'Anonymous'} says:
            </Author>
              <Timestamp theme={theme}>{formatDate(comment.created_at)}</Timestamp>
              <Content theme={theme}>{comment.content}</Content>
            </CommentItem>
          ))}
        </CommentsWrapper>
      )}

      <CommentForm
        postId={postId}
        onSuccess={handleCommentPosted}
        compact={!expanded}
      />
    </Section>
  );
};

export default CommentList;
